import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { userProfileAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  User, Mail, Phone, School, MapPin, Target, FileText, Save, 
  Trophy, Clock, BookOpen, TrendingUp, RefreshCw
} from 'lucide-react';

export default function ProfilePage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState({});
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    school: '',
    city: '',
    target_program: '',
    bio: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const [profileRes, statsRes] = await Promise.all([
        userProfileAPI.get(),
        userProfileAPI.getStats()
      ]);
      
      setProfile(profileRes.data.profile || {});
      setUserStats(statsRes.data);
      
      // Set form data
      setFormData({
        name: profileRes.data.user?.name || '',
        phone: profileRes.data.profile?.phone || '',
        school: profileRes.data.profile?.school || '',
        city: profileRes.data.profile?.city || '',
        target_program: profileRes.data.profile?.target_program || '',
        bio: profileRes.data.profile?.bio || ''
      });
    } catch (error) {
      console.error('Failed to load profile:', error);
      toast.error('Gagal memuat profil');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await userProfileAPI.update(formData);
      toast.success('Profil berhasil disimpan');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Gagal menyimpan profil');
    } finally {
      setSaving(false);
    }
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}j ${minutes}m`;
    return `${minutes} menit`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-[#4472C4]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-2xl font-bold text-slate-900 mb-6">Profil Saya</h1>
        
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Profile Form */}
          <div className="lg:col-span-2">
            <Card className="p-6">
              <h2 className="text-lg font-semibold mb-4">Informasi Pribadi</h2>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <User className="w-4 h-4" /> Nama Lengkap
                  </label>
                  <Input 
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Nama lengkap"
                    data-testid="profile-name-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Mail className="w-4 h-4" /> Email
                  </label>
                  <Input value={user?.email || ''} disabled className="bg-slate-100" />
                  <p className="text-xs text-slate-500 mt-1">Email tidak dapat diubah</p>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Phone className="w-4 h-4" /> Nomor HP
                  </label>
                  <Input 
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="08xxxxxxxxxx"
                    data-testid="profile-phone-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <School className="w-4 h-4" /> Sekolah/Institusi
                  </label>
                  <Input 
                    value={formData.school}
                    onChange={(e) => setFormData({...formData, school: e.target.value})}
                    placeholder="Nama sekolah atau institusi"
                    data-testid="profile-school-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <MapPin className="w-4 h-4" /> Kota
                  </label>
                  <Input 
                    value={formData.city}
                    onChange={(e) => setFormData({...formData, city: e.target.value})}
                    placeholder="Kota tempat tinggal"
                    data-testid="profile-city-input"
                  />
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <Target className="w-4 h-4" /> Target Program
                  </label>
                  <select 
                    className="w-full border rounded-md p-2"
                    value={formData.target_program}
                    onChange={(e) => setFormData({...formData, target_program: e.target.value})}
                    data-testid="profile-program-select"
                  >
                    <option value="">Pilih program target</option>
                    <option value="UTBK-SNBT">UTBK-SNBT</option>
                    <option value="CPNS">CPNS</option>
                    <option value="TNI">TNI</option>
                    <option value="POLRI">POLRI</option>
                    <option value="Sekolah-Kedinasan">Sekolah Kedinasan</option>
                    <option value="BUMN">BUMN</option>
                    <option value="TKA">TKA</option>
                    <option value="SMA">SMA Favorit</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Bio
                  </label>
                  <textarea 
                    className="w-full border rounded-md p-2 text-sm"
                    rows={3}
                    value={formData.bio}
                    onChange={(e) => setFormData({...formData, bio: e.target.value})}
                    placeholder="Ceritakan sedikit tentang dirimu..."
                    data-testid="profile-bio-input"
                  />
                </div>
                
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full bg-[#4472C4] hover:bg-[#3861a8]"
                  data-testid="save-profile-btn"
                >
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Simpan Profil
                </Button>
              </div>
            </Card>
          </div>
          
          {/* Stats Sidebar */}
          <div className="space-y-4">
            {/* User Code Card */}
            <Card className="p-4 bg-[#4472C4] text-white">
              <p className="text-sm opacity-80">Kode Siswa</p>
              <p className="text-2xl font-bold">{user?.user_code || '-'}</p>
            </Card>
            
            {/* Stats Card */}
            {userStats && (
              <Card className="p-4">
                <h3 className="font-semibold mb-4">Statistik Belajar</h3>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-600">
                      <BookOpen className="w-4 h-4" />
                      <span className="text-sm">Total Try Out</span>
                    </div>
                    <span className="font-bold">{userStats.total_exams}</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-600">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-sm">Rata-rata Skor</span>
                    </div>
                    <span className="font-bold">{userStats.average_score}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Trophy className="w-4 h-4" />
                      <span className="text-sm">Skor Terbaik</span>
                    </div>
                    <span className="font-bold text-green-600">{userStats.best_score}%</span>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Clock className="w-4 h-4" />
                      <span className="text-sm">Waktu Belajar</span>
                    </div>
                    <span className="font-bold">{formatTime(userStats.total_time_spent)}</span>
                  </div>
                </div>
                
                {userStats.programs_attempted?.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <p className="text-sm text-slate-600 mb-2">Program yang Diikuti:</p>
                    <div className="flex flex-wrap gap-1">
                      {userStats.programs_attempted.map((prog, i) => (
                        <Badge key={i} variant="secondary" className="text-xs">{prog}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
